import {APIService} from "./api.service";
import {map, Observable} from "rxjs";
import {IGameInstanceSummaryReport} from "../interfaces/IGameInstanceSummaryReport";
import {Injectable} from "@angular/core";
import {IGetRaffleReportResult} from "../interfaces/reporting/IGetRaffleReportResult";
import {IPlayersAndTicketsPerGameInstance} from "../interfaces/reporting/IPlayersAndTicketsPerGameInstance";
import {IPlayerRetentionReportResult} from "../interfaces/reporting/IPlayerRetentionReportResult";
import {IHourlyTicketSalesPerHourPerGame} from "../interfaces/reporting/IHourlyTicketSalesPerHourPerGame";
import {IManualAndAutoPlayTickets} from "../interfaces/reporting/IManualAndAutoPlayTickets";
import {ISalesPerDay} from "../interfaces/reporting/ISalesPerDay";
import {ICharitySalesPerDraw} from "../interfaces/reporting/ICharitySalesPerDraw";
import {IComprehensiveReport} from "../interfaces/reporting/IComprehensiveReport";
import {ILotteryLicenseReport} from "../interfaces/reporting/ILotteryLicenseReport";
import {IGameCharitySupporterBreakdown} from "../interfaces/reporting/IGameCharitySupporterBreakdown";
import {IGameInstanceShared} from "../interfaces/IGameInstanceShared";
import {IPlayedVsUnplayedPerDateRange} from "../interfaces/reporting/IPlayedVsUnplayedPerDateRange";
import {IPlayersPerDateRange} from "../interfaces/reporting/IPlayersPerDateRange";
import {IStandardGameReportPerDateRange} from "../interfaces/IStandardGameReportPerDateRange";
import {HttpParams} from "@angular/common/http";
import {IPayoutReportParams} from "../interfaces/reporting/IPayoutReportParams";
import {IPayoutReportResponse} from "../interfaces/reporting/IPayoutReportResponse";

@Injectable({
  providedIn: 'root'
})
export class ReportingService {
  constructor(private apiService: APIService) {
  }

  public getStandardGameInstanceReportData(gameInstanceP: IGameInstanceShared): Observable<IGameInstanceSummaryReport> {
    return this.apiService.MakeGetRequest<IGameInstanceSummaryReport>(`report/game-instance-report/for-game/${gameInstanceP.GameId}/by-id/${gameInstanceP.Id}`).pipe(map(res => res));
  }

  public getMultipleStandardGameInstanceReports(dateRangeDataP: IStandardGameReportPerDateRange): Observable<IGameInstanceSummaryReport[]> {
    let queryParams: HttpParams = new HttpParams();
    queryParams = queryParams.append("GameId", dateRangeDataP.GameId);
    queryParams = queryParams.append("StartDate", dateRangeDataP.StartDate.toISOString());
    queryParams = queryParams.append("EndDate", dateRangeDataP.EndDate.toISOString());
    return this.apiService.MakeGetRequest<IGameInstanceSummaryReport[]>(`report/game-instance-report/by-date-range`, queryParams).pipe(map(res => res));
  }

  public getRafflingReportData(gameInstanceP: IGameInstanceShared): Observable<IGetRaffleReportResult> {
    return this.apiService.MakeGetRequest<IGetRaffleReportResult>(`game-instance/players-with-tickets/game/${gameInstanceP.GameId}/${gameInstanceP.Id}`);
  }

  public getPlayedVsNotPlayedTicketsForDateRange(gameIdp: string, startDateP: string, endDateP: string): Observable<IPlayedVsUnplayedPerDateRange[]> {
    return this.apiService.MakeGetRequest<IPlayedVsUnplayedPerDateRange[]>(`report/played-vs-not-played/${gameIdp}/${startDateP}/${endDateP}`);
  }

  public getPlayersPerInstanceForDateRange(gameIdp: string, startDateP: string, endDateP: string): Observable<IPlayersPerDateRange[]> {
    return this.apiService.MakeGetRequest<IPlayersPerDateRange[]>(`report/players-per-instance/${gameIdp}/${startDateP}/${endDateP}`);
  }

  public getComprehensiveGameReportData(gameIdP: string, startDateP: string, endDateP: string): Observable<IComprehensiveReport> {
    return this.apiService.MakeGetRequest<IComprehensiveReport>(`report/comprehensive-report/${gameIdP}/${startDateP}/${endDateP}`);
  }

  public getLotteryLicenseReport(gameIdP: string, lotteryLicenseIdP: string, startDateP: string, endDateP: string): Observable<ILotteryLicenseReport> {
    return this.apiService.MakeGetRequest<ILotteryLicenseReport>(`report/lottery-license-report/${gameIdP}/${lotteryLicenseIdP}/${startDateP}/${endDateP}`);
  }

  public fetchSalesPerGameReport(gameIdP: string): Observable<IPlayerRetentionReportResult[]> {
    return this.apiService.MakeGetRequest<IPlayerRetentionReportResult[]>(`report/sales-per-game/${gameIdP}`);
  }

  public fetchGameCharitySupporterBreakdown(gameIdP: string): Observable<IGameCharitySupporterBreakdown[]> {
    return this.apiService.MakeGetRequest<IGameCharitySupporterBreakdown[]>(`report/game-charity-supporters-breakdown/${gameIdP}`);
  }

  public hourlyTicketSalesBreakdown(gameIdP: string): Observable<IHourlyTicketSalesPerHourPerGame[]> {
    return this.apiService.MakeGetRequest<IHourlyTicketSalesPerHourPerGame[]>(`report/hourly-ticket-sales/${gameIdP}`);
  }

  public getManualVsAutoPlayTickets(gameIdP: string, gameInstanceIdP: string): Observable<IManualAndAutoPlayTickets> {
    return this.apiService.MakeGetRequest<IManualAndAutoPlayTickets>(`report/manual-vs-autoplay-tickets/${gameInstanceIdP}/${gameIdP}`);
  }

  public salesPerDay(gameIdP: string, gameInstanceIdP: string): Observable<ISalesPerDay[]> {
    return this.apiService.MakeGetRequest<ISalesPerDay[]>(`report/sales-per-day/${gameInstanceIdP}/${gameIdP}`);
  }

  public topTenCharityContributors(gameIdP: string, gameInstanceIdP: string): Observable<ICharitySalesPerDraw[]> {
    return this.apiService.MakeGetRequest<ICharitySalesPerDraw[]>(`report/top-charity-contributors/${gameInstanceIdP}/${gameIdP}`);
  }

  public getSalesSummaryByPricePointReport(gameIdP: string, startDateP: string, endDateP: string): Observable<any> {
    return this.apiService.MakeGetRequest<any>(`report/sales-summary-by-price-point/${gameIdP}/${startDateP}/${endDateP}`);
  }

  public getPayoutReport(params: IPayoutReportParams): Observable<IPayoutReportResponse> {
    let queryParams: HttpParams = new HttpParams();
    queryParams = queryParams.append("GameId", params.gameId);
    queryParams = queryParams.append("IncludeWinnerPayouts", params.includeWinnerPayouts.toString());
    queryParams = queryParams.append("IncludeCharityPayouts", params.includeCharityPayouts.toString());
    queryParams = queryParams.append("UseDateRange", params.useDateRange.toString());
    queryParams = queryParams.append("StartDate", params.startDate);

    if (params.endDate) {
      queryParams = queryParams.append("EndDate", params.endDate);
    }

    queryParams = queryParams.append("CharityPayoutVoidedFilter", params.charityPayoutVoidedFilter);
    queryParams = queryParams.append("WinnerPayoutStatusFilter", params.winnerPayoutStatusFilter);
    queryParams = queryParams.append("DateFilterType", params.dateFilterType);

    return this.apiService.MakeGetRequest<IPayoutReportResponse>(`report/payout-report`, queryParams).pipe(map(res => res));
  }
}
